import { useSelector, useDispatch } from "react-redux";
import { arrayMove } from "@dnd-kit/sortable";
import { Row } from "@tanstack/react-table";
import { Dispatch, SetStateAction } from "react";

import { RootState } from "src/redux/reducers";
import { TasksColumn } from "src/redux/task/taskReducer";
import { generateRankString } from "src/utils/rankStrings";
import {
  setSelectedTaskColumns,
  updateListTaskRank,
  updateTaskMetaFieldRank,
  updateTasksList,
} from "src/redux";
import { TableTaskData } from "../types";

interface DragAndDropProps {
  rows: Row<{
    id: string;
  }>[];
  setData: Dispatch<SetStateAction<TableTaskData[]>>;
  setIsDragging: Dispatch<SetStateAction<boolean>>;
  draggableColumns: any[];
  projectId: string;
}

export default function useDragAndDrop({
  rows,
  setData,
  setIsDragging,
  draggableColumns,
  projectId,
}: DragAndDropProps) {
  const tasksColumns: TasksColumn[] = useSelector(
    (state: RootState) => state.taskReducer.tasksColumns,
  );
  const activeSortingColumn = useSelector(
    (state: RootState) => state.taskFiltersReducer.activeSortingColumn,
  );

  const selectedTaskColumns = useSelector(
    (state: RootState) => state.taskReducer.selectedTaskColumns,
  );

  const taskType = useSelector(
    (state: RootState) => state.projectReducer.taskType,
  );

  const dispatch = useDispatch();

  const moveRow = (oldIndex: number, newIndex: number) => {
    setData((data) => {
      return arrayMove(data, oldIndex, newIndex);
    });
  };

  const updateRowRank = (oldIndex: number, newIndex: number) => {
    if (activeSortingColumn || oldIndex === newIndex) return;

    const sourceFieldId = rows[oldIndex]?.id;

    let newRank = "";
    if (newIndex > oldIndex) {
      newRank = generateRankString(
        // @ts-ignore
        rows[newIndex + 1]?.original.rank || "",
        // @ts-ignore
        rows[newIndex]?.original.rank,
      );
    } else {
      newRank = generateRankString(
        // @ts-ignore
        rows[newIndex]?.original.rank,
        // @ts-ignore
        rows[newIndex - 1]?.original.rank || "",
      );
    }

    dispatch(updateListTaskRank(sourceFieldId, newRank));
  };

  const updateMetaFieldRank = async (fieldId: string, newRank: string) => {
    await dispatch(
      updateTaskMetaFieldRank({ metaFieldId: fieldId, metaFieldRank: newRank }),
    );
    dispatch(updateTasksList(projectId, taskType));
  };

  const handleColumnDragEndSingle = async ({
    oldIndex,
    newIndex,
  }: {
    oldIndex: number;
    newIndex: number;
  }) => {
    if (oldIndex === newIndex) {
      setIsDragging(false);
      return;
    }
    const sourceFieldId = draggableColumns[oldIndex]?.columnFromApi.metaFieldId;
    const targetFieldId = draggableColumns[newIndex]?.columnFromApi.metaFieldId;
    const fieldIndex = tasksColumns.findIndex(
      (x) => x.metaFieldId === targetFieldId,
    );
    let newRank = "";
    if (newIndex < oldIndex) {
      newRank = generateRankString(
        tasksColumns[fieldIndex - 1]?.metaFieldRank || "",
        tasksColumns[fieldIndex]?.metaFieldRank,
      );
      // move bottom
    } else {
      newRank = generateRankString(
        tasksColumns[fieldIndex]?.metaFieldRank,
        tasksColumns[fieldIndex + 1]?.metaFieldRank || "",
      );
    }

    updateMetaFieldRank(sourceFieldId, newRank);
    setIsDragging(false);
  };

  const handleColumnDragEndMulti = async ({
    oldIndex,
    newIndex,
  }: {
    oldIndex: number;
    newIndex: number;
  }) => {
    const ranksToUpdate = [];
    const targetFieldId = draggableColumns[newIndex]?.columnFromApi.metaFieldId;
    const targetElementIndex = draggableColumns.findIndex(
      (x: any) => x.id === targetFieldId,
    );

    // columns are already sorted, but we need a copy of the array, not the actual one
    const sortedSelectedColumns = [...selectedTaskColumns];
    const draggableColumnsCopy = [...draggableColumns];

    const getElementRankByIndex = (index: number) => {
      return draggableColumnsCopy[index]?.columnFromApi?.metaFieldRank;
    };

    let currentField = sortedSelectedColumns[0];
    let previousElementRank = "";
    let newRank = "";

    for (let i = 0; i < sortedSelectedColumns.length; i++) {
      currentField = sortedSelectedColumns[i];

      const oldFieldIndex = draggableColumnsCopy.findIndex(
        (x: { id: string }) => x.id === currentField.metaFieldId,
      );

      const filteredColumns = draggableColumnsCopy.filter(
        (column: { id: string }) =>
          column.id === currentField.metaFieldId ||
          !selectedTaskColumns.find((item) => item.metaFieldId === column.id),
      );

      const difference = newIndex - oldIndex + i;

      const newFieldIndex =
        filteredColumns.findIndex(
          (column: { id: string }) => column.id === currentField.metaFieldId,
        ) + difference;

      if (oldFieldIndex === newFieldIndex) {
        continue;
      }

      if (newIndex === oldIndex) {
        const leftBorderRank =
          getElementRankByIndex(targetElementIndex - 1) || "";
        const rightBorderRank =
          getElementRankByIndex(targetElementIndex + 1) || "";

        if (i === 0) {
          previousElementRank = leftBorderRank;
        }

        newRank = generateRankString(previousElementRank, rightBorderRank);
      } else if (newIndex < oldIndex) {
        const leftBorderRank =
          getElementRankByIndex(targetElementIndex - 1) || "";
        const rightBorderRank = getElementRankByIndex(targetElementIndex) || "";

        if (i === 0) {
          previousElementRank = leftBorderRank;
        }

        newRank = generateRankString(previousElementRank, rightBorderRank);
      } else {
        const leftBorderRank = getElementRankByIndex(targetElementIndex) || "";
        const rightBorderRank =
          getElementRankByIndex(targetElementIndex + 1) || "";

        if (i === 0) {
          previousElementRank = leftBorderRank;
        }

        newRank = generateRankString(previousElementRank, rightBorderRank);
      }

      previousElementRank = newRank;

      if (currentField.metaFieldRank !== newRank) {
        ranksToUpdate.push({
          metaFieldId: currentField.metaFieldId,
          metaFieldRank: newRank,
        });
      }
    }

    if (ranksToUpdate.length > 0) {
      dispatch(setSelectedTaskColumns([]));
      dispatch(updateTaskMetaFieldRank(ranksToUpdate));
    }
    setIsDragging(false);
  };

  const handleColumnDragEnd = async ({
    oldIndex,
    newIndex,
  }: {
    oldIndex: number;
    newIndex: number;
  }) => {
    const sourceFieldId = draggableColumns[oldIndex]?.columnFromApi.metaFieldId;

    if (
      selectedTaskColumns.find((item) => item.metaFieldId === sourceFieldId)
    ) {
      handleColumnDragEndMulti({ oldIndex, newIndex });
    } else {
      handleColumnDragEndSingle({ oldIndex, newIndex });
    }
  };

  const handleRowDragEnd = ({
    oldIndex,
    newIndex,
  }: {
    oldIndex: number;
    newIndex: number;
  }) => {
    if (oldIndex !== newIndex) {
      moveRow(oldIndex, newIndex);
      updateRowRank(oldIndex, newIndex);
    }
  };

  const moveColumnsToTheLeftEnd = () => {
    const lastSelectedColumnIndex = draggableColumns.findIndex(
      (column: any) =>
        column.columnFromApi.metaFieldId ===
        selectedTaskColumns[selectedTaskColumns.length - 1]?.metaFieldId,
    );

    const firstDraggableColumnIndex = 0;

    handleColumnDragEndMulti({
      oldIndex: lastSelectedColumnIndex,
      newIndex: firstDraggableColumnIndex,
    });
  };

  const moveColumnsToTheRightEnd = () => {
    const firstSelectedColumnIndex = draggableColumns.findIndex(
      (column: any) =>
        column.columnFromApi.metaFieldId ===
        selectedTaskColumns[0]?.metaFieldId,
    );

    const lastDraggableColumnIndex = draggableColumns.length - 1;

    handleColumnDragEndMulti({
      oldIndex: firstSelectedColumnIndex,
      newIndex: lastDraggableColumnIndex,
    });
  };

  return {
    handleColumnDragEnd,
    handleRowDragEnd,
    moveColumnsToTheLeftEnd,
    moveColumnsToTheRightEnd,
  };
}
